import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import UserAvatar from '../../components/UserAvatar';
import { authAPI, usersAPI } from '../../services/api';
import { formatUserDisplayName } from '../../utils/userDisplayName';
import './Profile.css';

const formatDate = (value, language) => {
  if (!value) return '-';

  let date;
  const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    date = new Date(Number(year), Number(month) - 1, Number(day));
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) return String(value);

  const locale = language === 'nl' ? 'nl-NL' : 'en-US';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

const Profile = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const [profile, setProfile] = useState(user || null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      if (!user?.id) return;
      setIsLoading(true);
      setLoadError('');

      try {
        const response = await usersAPI.getById(user.id);
        if (isMounted) {
          setProfile(response);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message || t('student.profile.loadError'));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [t, user?.id]);

  const profileRows = useMemo(() => {
    const studentProfile = profile?.studentProfile || {};

    return [
      [t('common.name'), formatUserDisplayName(profile)],
      [t('student.profile.firstName'), profile?.firstName || '-'],
      [t('common.email'), profile?.email || '-'],
      [t('admin.location'), profile?.location || '-'],
      [t('common.phone'), studentProfile.mobile || profile?.phone || '-'],
      [t('student.profile.telephone'), studentProfile.telephone || '-'],
      [t('common.address'), profile?.address || '-'],
      [t('student.profile.city'), studentProfile.city || '-'],
      [t('student.profile.dateOfBirth'), formatDate(profile?.dateOfBirth, i18n.language)],
      [t('student.profile.placeOfBirth'), studentProfile.placeOfBirth || '-'],
      [t('student.profile.familyName'), studentProfile.familyName || profile?.lastName || '-'],
      [t('student.profile.maidenName'), studentProfile.maidenName || '-'],
      [t('student.profile.otherNames'), studentProfile.otherNames || '-'],
      [t('student.profile.education'), studentProfile.opleiding || '-'],
      [t('student.profile.previousEducation'), studentProfile.vooropleiding || '-'],
      [t('student.profile.previousEducationOther'), studentProfile.vooropleidingAnders || '-'],
      [t('student.profile.relevantEducations'), studentProfile.relevantOpleidingen || '-'],
      [t('student.profile.relevantTrainings'), studentProfile.relevantTrainingen || '-'],
      [t('student.profile.workDeclaration'), studentProfile.relevanteWerkverklaring || '-']
    ];
  }, [i18n.language, profile, t]);

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setPasswordMessage('');
    setPasswordError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('student.profile.password.fillAllFields'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t('student.profile.password.noMatch'));
      return;
    }

    if (String(newPassword).length < 6) {
      setPasswordError(t('student.profile.password.minLength'));
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError(t('student.profile.password.mustDiffer'));
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const result = await authAPI.changePassword(currentPassword, newPassword);
      setPasswordMessage(result?.message || t('student.profile.password.success'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordError(error.message || t('student.profile.password.error'));
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div className="student-profile-page">
      <div className="student-profile-header">
        <h1>{t('student.profile.title')}</h1>
        <p>{t('student.profile.subtitle')}</p>
      </div>

      <div className="student-profile-grid">
        <section className="student-profile-card">
          <div className="student-profile-identity">
            <UserAvatar user={profile || user} size="xl" className="student-profile-avatar" />
            <div>
              <h2>{formatUserDisplayName(profile || user)}</h2>
              <p>{profile?.email || user?.email || '-'}</p>
            </div>
          </div>

          {isLoading && <p className="student-profile-state">{t('common.loading')}</p>}
          {loadError && <p className="student-profile-error">{loadError}</p>}

          {!isLoading && (
            <dl className="student-profile-details">
              {profileRows.map(([label, value]) => (
                <React.Fragment key={label}>
                  <dt>{label}</dt>
                  <dd>{value || '-'}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </section>

        <section className="student-profile-card">
          <h2>{t('student.profile.password.title')}</h2>
          <p className="student-profile-password-help">{t('student.profile.password.subtitle')}</p>

          <form className="student-profile-password-form" onSubmit={handleChangePassword}>
            <label htmlFor="currentPassword">{t('student.profile.password.current')}</label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
            />

            <label htmlFor="newPassword">{t('student.profile.password.new')}</label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
            />

            <label htmlFor="confirmPassword">{t('student.profile.password.confirm')}</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
            />

            {passwordError && <p className="student-profile-error">{passwordError}</p>}
            {passwordMessage && <p className="student-profile-success">{passwordMessage}</p>}

            <button type="submit" className="student-profile-submit" disabled={isUpdatingPassword}>
              {isUpdatingPassword ? t('student.profile.password.saving') : t('student.profile.password.save')}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default Profile;
